/**
 * Auth OTP Composable
 * OTP 驗證流程管理
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/auth/authOtp.js)
 * - 單例模式：使用 nuxtApp._authOtpContext 避免 storeToRefs/computed/watch 重複創建
 */

import { storeToRefs } from 'pinia'
import { ref, computed, watch } from 'vue'

import { otpApi } from '@/api'
import { useSite } from '@/composables/core/useSite'
import { useUserInfo } from '@/composables/user/useUserInfo'
import { useAuthOtpStore } from '@/stores/auth/authOtp'
import { useMainStore } from '@/stores/main'

export const useAuthOtp = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 storeToRefs、computed 和 watch 只創建一次，避免 SSR 環境下記憶體洩漏
  if (!nuxtApp._authOtpContext) {
    const { $request } = nuxtApp
    const store = useAuthOtpStore()
    const mainStore = useMainStore()
    const { setModal, setModalLoading, setVerifyModal } = mainStore
    const { setVerifyStrategy, setVerifyCheckpoint } = store
    const { userInfo, fetchUserInfo, phoneVerificationStatus, emailVerificationStatus } = useUserInfo()
    const { otpCodeLength, registerAutoPassOTPEnable } = useSite()

    // storeToRefs - 只調用一次
    const storeRefs = storeToRefs(store)
    const { strategy, error, countryCode, phone, email, target, requestId, ttl, needPhone, needEmail, loading, providerQueue, currentProviderIndex } =
      storeRefs

    // 標記玩家是否已主動提交 OTP（用於區分玩家驗證 vs 客服手動通過）
    const userAcknowledged = ref(false)

    // 保存 watch stop 函數，用於 cleanup 時停止所有 watch
    const stopWatchers = []

    // computed - 只創建一次

    // 當前 provider 是否已驗證（基於 phone/email 個別狀態）
    const isCurrentProviderVerified = computed(() => {
      if (strategy.value === 'phone') return phoneVerificationStatus.value
      if (strategy.value === 'email') return emailVerificationStatus.value
      return false
    })

    // 多步驟流程：是否還有下一個待驗證的 provider
    const hasNextProvider = computed(() => currentProviderIndex.value < providerQueue.value.length - 1)

    const showOtpVerification = computed(() => {
      return !needPhone.value && !needEmail.value && !isCurrentProviderVerified.value && !!requestId.value
    })

    const allowResend = computed(() => {
      if (strategy.value === 'email') {
        return ttl.value <= 25 * 60 * 1000
      } else {
        return ttl.value <= 4 * 60 * 1000
      }
    })

    const checkVerificationStatus = async () => {
      if (!userInfo.value) return

      if (isCurrentProviderVerified.value) return

      if (strategy.value === 'phone' && !userInfo.value.tel) {
        store.setNeedPhone(true)
        return
      }

      if (strategy.value === 'email' && !userInfo.value.email) {
        store.setNeedEmail(true)
        return
      }

      await initiateVerification()
    }

    const initiateVerification = async () => {
      // auto-pass 模式下後端自行判斷 provider，不需要 strategy；一般模式下 strategy 必須存在
      if (!registerAutoPassOTPEnable.value && !strategy.value) return

      try {
        if (userInfo.value.email) {
          store.setEmail(userInfo.value.email)
        }
        if (userInfo.value.country_code) {
          store.setCountryCode(userInfo.value.country_code)
        }
        if (userInfo.value.tel) {
          store.setPhone(userInfo.value.tel)
        }

        // auto-pass 模式：後端自行決定驗證方式，不帶任何參數
        const verifyParams = registerAutoPassOTPEnable.value
          ? {}
          : {
              provider: strategy.value,
              countryCode: userInfo.value.country_code,
              phone: userInfo.value.tel,
              email: userInfo.value.email,
            }

        const data = await $request.send(otpApi.verify(verifyParams))

        store.setRequestId(data.request_id)
        store.setTtl(data.ttl)
        store.setTarget(data.target)
        store.setNeedAcknowledge(data.needAcknowledge)
        store.startCountdown()

        if (!data.needAcknowledge) {
          await fetchUserInfo()
        }

        return data
      } catch (error) {
        store.setError(error)
        if (error.code === 'ERR_F106') {
          store.setNeedPhone(true)
        }
        console.error('[useAuthOtp] initiateVerification error:', error)
        throw error
      }
    }

    const sendPhone = async (countryCode, phone) => {
      store.setLoading(true)
      try {
        store.setCountryCode(countryCode)
        store.setPhone(phone)

        const data = await $request.send(
          otpApi.verify({
            provider: 'phone',
            countryCode,
            phone,
          })
        )

        store.setRequestId(data.request_id)
        store.setTtl(data.ttl)
        store.setTarget({
          provider: 'phone',
          email: data.email,
          countryCode: data.countryCode,
          phone: data.phone,
        })
        store.setNeedAcknowledge(data.needAcknowledge)

        store.setNeedPhone(false)
        store.startCountdown()
        return data
      } catch (error) {
        console.error('[useAuthOtp] sendPhone error:', error)
        throw error
      } finally {
        store.setLoading(false)
      }
    }

    const sendEmail = async (email) => {
      store.setLoading(true)
      try {
        store.setEmail(email)

        const data = await $request.send(
          otpApi.verify({
            provider: 'email',
            email,
          })
        )

        store.setRequestId(data.request_id)
        store.setTtl(data.ttl)
        store.setTarget({
          provider: 'email',
          email: email,
          countryCode: data.countryCode,
          phone: data.phone,
        })
        store.setNeedAcknowledge(data.needAcknowledge)

        store.setNeedEmail(false)
        store.startCountdown()
        return data
      } catch (error) {
        console.error('[useAuthOtp] sendEmail error:', error)
        throw error
      } finally {
        store.setLoading(false)
      }
    }

    const resetOtp = async () => {
      try {
        const data = await $request.send(otpApi.resetOtp({ request_id: requestId.value }))

        store.setRequestId(data.request_id)
        store.setTtl(data.ttl)
        store.setTarget({
          provider: strategy.value,
          email: data.email,
          countryCode: data.countryCode,
          phone: data.phone,
        })
        store.setNeedAcknowledge(data.needAcknowledge)

        store.startCountdown()

        return data
      } catch (error) {
        console.error('[useAuthOtp] resetOtp error:', error)
        throw error
      }
    }

    const acknowledge = async (code) => {
      store.setLoading(true)
      try {
        await $request.send(
          otpApi.acknowledge({
            request_id: requestId.value,
            code,
          })
        )

        userAcknowledged.value = true

        // 等個 2 秒，再更新用戶資料
        await new Promise((resolve) => setTimeout(resolve, 2000))

        const userInfoData = await fetchUserInfo()
        store.stopCountdown()

        // 依當前 provider 檢查對應驗證狀態（API 回傳可能是 boolean 或 number，統一用 !!）
        const isVerifiedNow = strategy.value === 'phone' ? !!userInfoData.phone_verification_status : !!userInfoData.email_verification_status

        if (!isVerifiedNow) {
          // 驗證狀態如果還沒更新，再等個 2 秒，確保用戶資料更新完成再關掉畫面
          await new Promise((resolve) => setTimeout(resolve, 2000))
        }

        return true
      } catch (error) {
        console.error('[useAuthOtp] acknowledge error:', error)
        throw error
      } finally {
        store.setLoading(false)
      }
    }

    const reset = () => {
      userAcknowledged.value = false
      store.resetState()
    }

    const closeMandatoryVerify = () => {
      setVerifyModal(null)
      cleanupVerifyState()
    }

    const closeVerify = () => {
      setModal(null)
      cleanupVerifyState()
    }

    const cleanupVerifyState = () => {
      setModalLoading(false)
      setVerifyStrategy(null)
      setVerifyCheckpoint(null)
    }

    const initializeVerification = async (strategyValue) => {
      store.setStrategy(strategyValue)
      store.setLoading(true)

      try {
        await fetchUserInfo()
        await checkVerificationStatus()
        return true
      } catch (error) {
        console.error('[useAuthOtp] initializeVerification error:', error)
        return false
      } finally {
        store.setLoading(false)
      }
    }

    /**
     * 切換到下一個待驗證的 provider（多步驟流程內部使用）
     * 重置 OTP 狀態並以下一個 provider 重新初始化，modal 不關閉
     */
    const moveToNextProvider = async () => {
      store.advanceProvider()
      const next = providerQueue.value[currentProviderIndex.value]

      // 重置 OTP 狀態（保留 providerQueue/currentProviderIndex）
      store.setRequestId(null)
      store.setTtl(0)
      store.setTarget(null)
      store.setNeedAcknowledge(true)
      store.setNeedPhone(false)
      store.setNeedEmail(false)
      store.setError(null)
      setVerifyStrategy(next)
      userAcknowledged.value = false

      await initializeVerification(next)
    }

    const handleVerificationSuccess = async (analyticsData, isMandatory = false, onLogout = null) => {
      const { $analytics } = nuxtApp

      if (analyticsData) {
        $analytics.push({
          event: 'otp_verification_success',
          ...analyticsData,
        })
      }

      if (isMandatory) {
        if (onLogout && typeof onLogout === 'function') {
          await onLogout()
        }
        closeMandatoryVerify()
      } else {
        reset()
        closeVerify()
      }
    }

    const openVerifyModal = ({ method, checkPoint }, isMandatory) => {
      setVerifyStrategy(method)
      setVerifyCheckpoint(checkPoint)

      if (isMandatory) {
        setVerifyModal('ModalVerifyMandatory')
      } else {
        setModal('ModalVerify')
      }
    }

    const triggerOtpVerification = async ({ providers, checkPoint }, isMandatory = false) => {
      // auto-pass 模式：直接打 API，後端決定是否需要玩家手動輸入 OTP（needAcknowledge）
      // 若不需輸入（needAcknowledge = false）則直接通過，否則 fall-through 至 modal 流程
      if (registerAutoPassOTPEnable.value) {
        try {
          const data = await initiateVerification()
          if (!data.needAcknowledge) {
            return true
          }
        } catch (error) {
          console.error('[useAuthOtp] Auto verification failed:', error)
        }
      }

      // 一般模式（或 auto-pass needAcknowledge = true）：開啟 modal 等待玩家輸入
      return new Promise((resolve) => {
        store.setPendingResolver(resolve)
        store.setProviderQueue(providers)
        store.setCurrentProviderIndex(0)
        openVerifyModal({ method: providers[0], checkPoint }, isMandatory)
      })
    }

    // watch - 只創建一次，保存 stop 函數供 cleanup 使用
    stopWatchers.push(
      watch(loading, (newLoading) => {
        setModalLoading(newLoading)
      })
    )

    // cleanup 方法：停止所有 watch，供 SSR cleanup plugin 調用
    const cleanup = () => {
      stopWatchers.forEach((stop) => stop?.())
      store.stopCountdown()
    }

    nuxtApp._authOtpContext = {
      strategy,
      error,
      countryCode,
      phone,
      email,
      target,
      requestId,
      ttl,
      needPhone,
      needEmail,
      loading,
      providerQueue,
      currentProviderIndex,
      isCurrentProviderVerified,
      hasNextProvider,
      showOtpVerification,
      allowResend,
      otpCodeLength,
      sendPhone,
      sendEmail,
      resetOtp,
      acknowledge,
      reset,
      cleanupVerifyState,
      initializeVerification,
      moveToNextProvider,
      handleVerificationSuccess,
      triggerOtpVerification,
      userAcknowledged,
      closeMandatoryVerify,
      resolveOtpVerification: store.resolvePending,
      cleanup,
    }
  }

  return nuxtApp._authOtpContext
}
